import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  BRAND_COPY,
  INGREDIENTS_BY_TYPE,
  HOW_TO_USE,
  GLOW_TIP,
} from "../mock";
import { useProducts, useProduct, useFragrances } from "../hooks/useCatalog";
import { useCart } from "../context/CartContext";
import {
  Minus,
  Plus,
  ArrowUpRight,
  Truck,
  Shield,
  Leaf,
  Sparkles,
  Droplets,
  Zap,
  Clock,
  ShieldOff
} from "lucide-react";
import { toast } from "sonner";
import FragranceSelector from "../components/FragranceSelector";
import ProductReviews from "../components/ProductReviews";

const benefitIcons = [Droplets, Leaf, Zap, Sparkles];

const ProductDetail = () => {
  const { productId } = useParams();
  const { product } = useProduct(productId);
  const { products } = useProducts();
  const { fragrances: fragMap } = useFragrances();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [audience, setAudience] = useState("her");

  const fragrances = audience === "him" ? (fragMap.him || []) : (fragMap.her || []);
  const [fragCode, setFragCode] = useState(fragrances[0]?.code || "000");

  // Reset fragrance when audience changes to a valid code in the new list
  React.useEffect(() => {
    const list = audience === "him" ? (fragMap.him || []) : (fragMap.her || []);
    if (!list.find((f) => f.code === fragCode)) {
      setFragCode(list[0]?.code || "000");
    }
  }, [audience, fragCode, fragMap]);

  if (!product) return <Navigate to="/shop" replace />;

  const related = products.filter((p) => p.id !== product.id).slice(0, 3);
  const ingredients = INGREDIENTS_BY_TYPE[product.category] || INGREDIENTS_BY_TYPE["body-butter"];
  const howTo = HOW_TO_USE[product.category] || HOW_TO_USE["body-butter"];
  const activeFrag = fragrances.find((f) => f.code === fragCode) || fragrances[0];
  const isProductOOS = product.stock_status === "out_of_stock";
  const isFragOOS = activeFrag?.stock_status === "out_of_stock";
  const displayTagline = product.tagline;

  const isRoseTile =
    product.category === "body-butter" ||
    product.category === "spray-oil" ||
    product.category === "glow-trio";

  const handleAdd = () => {
    if (isProductOOS) return toast.error("This product is currently out of stock.");
    if (activeFrag && isFragOOS) return toast.error(`${activeFrag.name} is currently out of stock. Pick another fragrance.`);
    const forWho = audience === "him" ? "For Him" : "For Her";
    const scentSuffix = activeFrag ? ` · ${forWho} · ${activeFrag.code} ${activeFrag.name}` : "";
    addItem(
      { ...product, name: `${product.name}${scentSuffix}` },
      qty
    );
    toast.success(`${product.name}${activeFrag ? ` (${forWho} · ${activeFrag.code})` : ""} x${qty} — added to your cart.`);
  };

  return (
    <main className="min-h-screen bg-charcoal">
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto pt-12 pb-10">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-off-white/50 mb-8">
          <Link to="/" className="hover:text-rose">HOME</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-rose">SHOP</Link>
          <span>/</span>
          <span className="text-rose">{product.name.toUpperCase()}</span>
        </div>

        <div className="grid md:grid-cols-12 gap-8 md:gap-14">
          <div className="md:col-span-7">
            <div className={`aspect-[4/5] md:aspect-[5/6] border border-off-white/10 relative overflow-hidden flex items-center justify-center p-10 ${isRoseTile ? "tile-rose" : "tile-cream"}`}>
              <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
              {product.badge && (
                <span className="absolute top-6 left-6 font-mono text-[10px] tracking-[0.25em] px-3 py-1 bg-off-white text-charcoal font-semibold">
                  {product.badge}
                </span>
              )}
              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.3em] text-charcoal/70">// BATCH 087</span>
                <span className="font-mono text-[10px] tracking-[0.3em] text-charcoal/70">{product.size}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-2">
              {[product.image, product.image, product.image].map((src, i) => (
                <div key={i} className={`aspect-square border border-off-white/10 overflow-hidden flex items-center justify-center p-3 ${i % 2 === 0 ? "tile-cream" : "tile-rose"}`}>
                  <img src={src} alt="" className="w-full h-full object-contain opacity-90 hover:opacity-100 transition-opacity" />
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-4">
              // {product.category.toUpperCase().replace("-", " ")}
            </p>
            <h1 className="font-serif-display italic text-4xl md:text-6xl leading-[0.95] text-off-white mb-4">
              {product.name}
            </h1>
            <p className="text-off-white/70 text-lg font-light mb-6">{displayTagline}</p>

            {/* For Her / For Him toggle */}
            <div className="mb-6">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// CHOOSE YOUR EDITION</p>
              <div className="inline-flex border border-off-white/20 p-1">
                {[
                  { key: "her", label: "FOR HER" },
                  { key: "him", label: "FOR HIM" }
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setAudience(opt.key)}
                    className={`px-5 h-10 font-mono text-[11px] tracking-[0.25em] transition-colors-only ${
                      audience === opt.key
                        ? "bg-rose text-charcoal"
                        : "text-off-white/70 hover:text-off-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-display text-3xl text-off-white">${product.price.toFixed(2)}</span>
              {product.originalPrice && (
                <span className="font-mono text-lg text-off-white/40 line-through">${product.originalPrice.toFixed(2)}</span>
              )}
              <span className="font-mono text-[10px] tracking-[0.3em] text-off-white/55">USD · {product.size}</span>
            </div>

            {/* Why you'll love it */}
            <div className="border-l-2 border-rose pl-5 py-2 mb-8">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// WHY YOU'LL LOVE IT</p>
              <p className="text-off-white/85 text-[14px] leading-relaxed font-light">
                {BRAND_COPY.whyYouLoveIt}
              </p>
            </div>

            {/* Fragrance selector — only for scented products */}
            {product.category !== "glow-trio" && (
              <FragranceSelector
                fragrances={fragrances}
                selected={fragCode}
                onSelect={setFragCode}
              />
            )}

            {/* Qty + CTA */}
            <div className="flex items-stretch gap-3 mb-3 mt-6">
              <div className="inline-flex items-center border border-off-white/20">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-14 flex items-center justify-center text-off-white/70 hover:text-rose transition-colors-only"><Minus size={14} /></button>
                <span className="font-mono text-sm w-10 text-center text-off-white">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-11 h-14 flex items-center justify-center text-off-white/70 hover:text-rose transition-colors-only"><Plus size={14} /></button>
              </div>
              <button
                onClick={handleAdd}
                disabled={isProductOOS || isFragOOS}
                className="btn-blueprint flex-1 h-14 bg-off-white text-charcoal font-mono text-[12px] tracking-[0.25em] font-semibold flex items-center justify-center gap-2 hover:bg-rose transition-colors-only disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                {isProductOOS ? "OUT OF STOCK" : isFragOOS ? "FRAGRANCE OUT OF STOCK" : (<>ADD TO CART <ArrowUpRight size={16} /></>)}
              </button>
            </div>

            {/* Processing time */}
            <div className="flex items-center gap-2 mb-3" data-testid="processing-time">
              <Clock size={13} className="text-rose" strokeWidth={1.5} />
              <span className="font-mono text-[11px] tracking-[0.2em] text-off-white/70">
                PROCESSING · <span className="text-off-white">{product.processing_time}</span>
              </span>
            </div>
            <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 mb-6">
              // SHIPS AFTER PROCESSING · FREE OVER $75
            </p>

            {/* No refund policy notice */}
            <div className="border border-rose/40 bg-rose/[0.06] p-4 mb-8" data-testid="no-refund-notice">
              <div className="flex items-start gap-3">
                <ShieldOff size={16} className="text-rose shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-1">// NO REFUND ON GOODS</p>
                  <p className="text-off-white/75 text-[13px] leading-relaxed font-light">
                    All sales are final. Because our products are made-to-order in small batches with natural ingredients, we do not accept returns or issue refunds once shipped. You'll be asked to acknowledge this at checkout.
                  </p>
                </div>
              </div>
            </div>

            {/* Trust icons */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-off-white/10">
              {[
                { Icon: Truck, label: "48-HR SHIP" },
                { Icon: Shield, label: "MICRO-BATCH" },
                { Icon: Leaf, label: "100% NATURAL" }
              ].map(({ Icon, label }) => (
                <div key={label} className="flex flex-col items-center text-center gap-2 py-2">
                  <Icon size={18} className="text-rose" strokeWidth={1.5} />
                  <span className="font-mono text-[9px] tracking-[0.25em] text-off-white/60">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-16 border-t border-off-white/10">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// KEY BENEFITS</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] tracking-tighter text-off-white">
              Built for<br />the glow.
            </h2>
          </div>
          <ul className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {BRAND_COPY.keyBenefits.map((b, i) => {
              const Icon = benefitIcons[i % benefitIcons.length];
              return (
                <li key={i} className="border border-off-white/10 p-5 flex items-start gap-4 bg-off-white/[0.02]">
                  <span className="shrink-0 w-9 h-9 border border-rose/60 flex items-center justify-center">
                    <Icon size={16} className="text-rose" strokeWidth={1.5} />
                  </span>
                  <div>
                    <span className="font-mono text-[10px] tracking-[0.25em] text-rose">0{i + 1}</span>
                    <p className="text-off-white text-[15px] leading-snug mt-1">{b}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Ingredients + How To Use */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-16 border-t border-off-white/10">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// INGREDIENTS — {ingredients.label.toUpperCase()}</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] tracking-tighter text-off-white mb-6">
              Made with intention.
            </h2>
            <p className="text-off-white/60 font-light mb-6">{ingredients.note}</p>
            <ul className="space-y-2">
              {ingredients.list.map((ing) => (
                <li key={ing} className="flex items-center gap-3 text-off-white/85 text-[15px] border-b border-off-white/5 pb-2">
                  <span className="w-1.5 h-1.5 bg-rose" />
                  {ing}
                </li>
              ))}
            </ul>
            <div className="mt-6 border border-rose/30 p-4 bg-rose/[0.04]">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// FORMULA NOTE</p>
              <p className="text-off-white/75 text-[13px] leading-relaxed font-light">
                {BRAND_COPY.formulaDisclaimer}
              </p>
            </div>
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// HOW TO USE</p>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] tracking-tighter text-off-white mb-6">
              The ritual.
            </h2>
            <p className="text-off-white/85 text-lg font-light leading-relaxed mb-6">{howTo}</p>
            <div className="border border-rose/40 p-5 bg-rose/[0.06]">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// GLOW TIP</p>
              <p className="text-off-white/90 font-serif-display italic text-lg leading-snug">“{GLOW_TIP}”</p>
            </div>

            <div className="mt-8 border-t border-off-white/10 pt-6">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// {BRAND_COPY.returnPolicy.title.toUpperCase()}</p>
              <p className="text-off-white/70 text-sm font-light leading-relaxed">{BRAND_COPY.returnPolicy.body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Verified reviews */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto">
        <ProductReviews productId={product.id} />
      </section>

      {/* Related */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20 border-t border-off-white/10 bg-charcoal-2">
        <div className="mb-10">
          <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// PAIRS WITH</p>
          <h2 className="font-serif-display italic text-4xl md:text-5xl text-off-white">Stack the ritual.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {related.map((p, i) => (
            <Link
              to={`/product/${p.id}`}
              key={p.id}
              className="group border border-off-white/10 hover:border-off-white transition-colors-only bg-charcoal"
            >
              <div className={`aspect-[4/5] overflow-hidden flex items-center justify-center p-6 ${i % 2 === 0 ? "tile-rose" : "tile-cream"}`}>
                <img src={p.image} alt={p.name} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-4 bg-charcoal">
                <h3 className="font-serif-display italic text-xl text-off-white">{p.name}</h3>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-off-white/60 text-sm">{p.tagline}</span>
                  <span className="font-mono text-xs text-off-white">${p.price.toFixed(2)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
